import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { OrdersService } from '@src/app/shared/backend/clients';
import { BaseListComponent } from '@src/app/shared/components/base-list/base-list.component';
import { TableSettingService } from '@src/app/shared/services/table.setting.service';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { OrderState } from '@src/app/shared/enums/OrderState.enum';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { FiltersPanelChangeEvent, FiltersPanelFieldOverrides } from '@src/app/shared/components/filters-panel/filters-panel.models';
import { applyFiltersPanelChange } from '@src/app/shared/components/filters-panel/filters-panel.helper';
import { OrderStatePipe } from '@src/app/shared/pipes/orderState.pipe';
import { OrderGenerationTypePipe } from '@src/app/shared/pipes/orderGenerationType.pipe';
import { loadStoredPaginatorState, resetPaginatorStateToFirstPage } from '@app/shared/components/paginator/paginator-storage.helper';

@Component({
    selector: 'app-code-orders',
    templateUrl: './code-orders.component.html',
    styleUrl: './code-orders.component.scss',
    host: {
        class: 'app-code-orders'
    },
    standalone: false
})
export class CodeOrdersComponent extends BaseListComponent implements OnInit, OnDestroy {

  private initialLoadDone = false;
  private readonly componentDestroyed$ = new Subject<void>();
  public readonly codeOrdersTableName = TableSettingService.CodeOrdersTableName;
  public filtersPanelFieldOverrides: FiltersPanelFieldOverrides = {};

  // Dropdown: state
  private stateOptions: Array<{ id: string | number; text: string }> = [];

  // Dropdown: codeType
  private typeOptions: Array<{ id: string | number; text: string }> = [];

  allowCreateOrder: boolean = false;
  allowDownloadOrder: boolean = false;

  constructor(
    private router: Router,
    public override tableSettingService: TableSettingService,
    private toasterService: ToasterService,
    private changeDetector: ChangeDetectorRef,
    private ordersService: OrdersService,
    private permissionService: PermissionService,
    private translateService: TranslateService
  ) {
    super();
  }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.restorePaginatorStateFromStorage();

    this.allowCreateOrder = await this.permissionService.hasActionPermissions(Actions.create_orders);
    this.allowDownloadOrder = await this.permissionService.hasActionPermissions(Actions.download_orders);

    // i18n propagation on lang change
    this.rebuildStateOptions();
    this.rebuildTypeOptions();

    this.translateService.onLangChange
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.rebuildStateOptions();
        this.rebuildTypeOptions();
        this.rebuildFiltersPanelOverrides();

        // Update sort labels without resetting the current sort selection
        this.rebuildSortPanelColumnLabels();
      });

    await this.tableSettingService.init();
    await super.setCache(this.codeOrdersTableName);

    // default sort for stable pagination + 18n fn labelResolver (sort dropdown options)
    this.initSortPanel({
      defaultSort: { key: 'createdOn', direction: 'DESC' },
      labelResolver: (col) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        if (translated && translated !== translationKey) {
          return translated;
        }

        return col.header;
      }
    });

    this.rebuildSortPanelColumnLabels();
    this.rebuildFiltersPanelOverrides();

    // Fallback: if the panel doesn't restore anything, load unfiltered!!
    setTimeout(async () => {
      if (!this.initialLoadDone) {
        this.initialLoadDone = true;
        await this.get();
      }
    });
  }

  override onPageChange(event: any): void {
    this.initialLoadDone = true;
    super.onPageChange(event);
  }

  protected override onCriteriaResetRequested(): void {
    this.resetPaginatorStorage();
  }

  private resetPaginatorStorage(): void {
    resetPaginatorStateToFirstPage({
      tableName: this.codeOrdersTableName,
      pageSize: this.pageSize,
    });
  }

  private restorePaginatorStateFromStorage(): void {
    const stored = loadStoredPaginatorState(this.codeOrdersTableName);

    if (!stored) {
      return;
    }

    const storedPageNumber = stored.valuesByFieldKey.pageNumber;
    const storedPageSize = stored.valuesByFieldKey.pageSize;

    this.pageNumber = storedPageNumber;
    this.pageSize = storedPageSize;

    this.filter.pageNumber = storedPageNumber;
    this.filter.pageSize = storedPageSize;
  }

  async onFiltersPanelChange(evt: FiltersPanelChangeEvent): Promise<void> {
    // Important: pass all table cols (not only visible ones) to prevent hidden col filters from affecting results
    const currentFilters = this.filter.filters as any;

    const nextFilters = applyFiltersPanelChange(
      evt,
      currentFilters,
      this.allTableColumns
    );

    if (!nextFilters) {
      return;
    }

    this.filter.filters = nextFilters as any;

    const source = evt.source;
    const isApplyAction = source === 'apply';
    const isClearAction = source === 'clear';
    const shouldResetPaging = isApplyAction || isClearAction;

    if (shouldResetPaging) {
      const firstPageNumber = 1;

      this.pageNumber = firstPageNumber;
      this.filter.pageNumber = firstPageNumber;

      this.onCriteriaResetRequested();
    }

    this.initialLoadDone = true;
    await this.get();
  }

  refreshCodeOrders() {
    this.get();
  }

  override async get() {
    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.applySortToFilterRequest();

    this.page = await firstValueFrom(this.ordersService.apiOrdersFilterTablePost(this.filter));
    this.data = this.page.results;

    this.changeDetector.detectChanges();
  }

  private rebuildStateOptions(): void {
    const pipe = new OrderStatePipe(this.translateService);
    this.stateOptions = pipe.orderStatesList;
  }

  private rebuildTypeOptions(): void {
    const pipe = new OrderGenerationTypePipe(this.translateService);
    this.typeOptions = pipe.orderGenerationTypesList;
  }

  private rebuildFiltersPanelOverrides() {
    this.filtersPanelFieldOverrides = {
      state: {
        fieldLabel: 'TABLE.HEADER.CodeOrderState',
        isMultiselect: true,

        options: this.stateOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      },
      codeType: {
        fieldLabel: 'TABLE.HEADER.OrderType',
        isMultiselect: true,

        options: this.typeOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      }
    };
  }

  private rebuildSortPanelColumnLabels(): void {
    const visibleColumns = Array.isArray(this.tableColumns) ? this.tableColumns : [];

    // Update sort labels without resetting the current sort selection
    const nextSortableColumns = visibleColumns
      .filter((col: any) => {
        const field = String(col?.field ?? '').trim();
        return (
          field.length > 0 &&
          field.toLowerCase() !== 'actions' &&
          col?.sortable !== false
        );
      })
      .map((col: any) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        let label;
        if (translated && translated !== translationKey) {
          label = translated;
        } else {
          label = col.header;
        }

        return {
          key: col.field,
          label
        };
      });

    this.sortableColumns = nextSortableColumns;

    // clear active sort if points to col that is not available due to system settings update
    const allowedKeys = new Set(nextSortableColumns.map(col => col.key));
    if (this.sortPanelSort?.key && !allowedKeys.has(this.sortPanelSort.key)) {
      this.onSortPanelChange(null);
    }
  }

  isDownloadDisabled(status: string): boolean {
    return status !== OrderState.CODE_ORDER_PROCESSED && status !== OrderState.CODE_ORDER_DOWNLOADED;
  }

  async downloadCodeOrderFile(event: any, orderId: number) {
    this.ordersService.apiOrdersDownloadIdGet(orderId).subscribe({
      next: (response: any) => {
        const fileUrl = response.url;

        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = `order_${orderId}_codes.zip`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
      error: (err: any) => {
        console.error('Error during file download', err);
        this.toasterService.show('error', 'Error', err, 3000);
      }
    });
  }

  goToOrderDetails(event: any, orderId: string) {
    this.router.navigate([`/code-orders/${orderId}`]);
  }

  goToCreateCodeOrderForm() {
    this.router.navigate(['/code-orders/add']);
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
